import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  blogList,
  certificates,
  experienceList,
  studyPath,
  workList,
} from "../data.js";
import { localizeDateLabel, localizeField } from "../i18n.js";
import { getCertificateSlug } from "./CertificatesPage.jsx";

export default function SearchBar({ t, copy, language }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const searchItems = useMemo(() => buildSearchItems(copy, language), [copy, language]);
  const normalizedQuery = normalize(query);

  const results = useMemo(() => {
    if (!normalizedQuery) {
      return searchItems.slice(0, 8);
    }

    return searchItems
      .map((item) => ({ item, score: getMatchScore(item.searchText, normalizedQuery) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
      .slice(0, 8)
      .map(({ item }) => item);
  }, [normalizedQuery, searchItems]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsOpen(true);
        return;
      }

      if (!isTyping && event.key === "/") {
        event.preventDefault();
        setIsOpen(true);
        return;
      }

      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  function closeSearch() {
    setIsOpen(false);
    setQuery("");
  }

  function openResult(path) {
    navigate(path);
    closeSearch();
  }

  return (
    <div className="site-search">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="site-search-trigger"
        style={searchTriggerStyle(t)}
        aria-haspopup="dialog"
      >
        <span>{copy.searchLabel}</span>
        <kbd style={kbdStyle(t)}>Ctrl K</kbd>
      </button>

      {isOpen && (
        <div className="search-overlay" role="presentation" onMouseDown={closeSearch}>
          <section
            className="search-modal"
            role="dialog"
            aria-modal="true"
            aria-label={copy.searchLabel}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="search-input-wrap">
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && results[0]) {
                    openResult(results[0].path);
                  }
                }}
                placeholder={copy.searchPlaceholder}
                aria-label={copy.searchLabel}
              />
              <button type="button" onClick={closeSearch} style={closeButtonStyle(t)}>
                {copy.searchClose}
              </button>
            </div>

            <div className="search-results">
              {results.length > 0 ? (
                results.map((result) => (
                  <button
                    key={`${result.type}-${result.path}`}
                    type="button"
                    className="search-item"
                    onClick={() => openResult(result.path)}
                    style={resultButtonStyle(t)}
                  >
                    <span style={resultMetaStyle(t)}>{result.type}</span>
                    <span style={resultTitleStyle(t)}>{result.title}</span>
                    {result.description ? (
                      <span style={resultDescriptionStyle(t)}>{result.description}</span>
                    ) : null}
                  </button>
                ))
              ) : (
                <div className="search-empty" style={{ color: t.dim }}>
                  {copy.searchNoResults}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function buildSearchItems(copy, language) {
  const pages = [
    { title: copy.nav.home, type: copy.searchTypePage, path: "/", text: "home about skills" },
    { title: copy.nav.blog, type: copy.searchTypePage, path: "/blog", text: "blog articles writing" },
    { title: copy.nav.experience, type: copy.searchTypePage, path: "/experience", text: "experience work jobs" },
    { title: copy.nav.study, type: copy.searchTypePage, path: "/study", text: "study education school" },
    { title: copy.nav.certificates, type: copy.searchTypePage, path: "/certificates", text: "certificates credentials" },
    { title: copy.nav.projects, type: copy.searchTypePage, path: "/projects", text: "projects work portfolio" },
    { title: copy.nav.contact, type: copy.searchTypePage, path: "/contact", text: "contact email github" },
  ];

  const projects = workList.map((item) => {
    const title = localizeField(item, "title", language);
    const description = [item.year, localizeField(item, "subtitle", language)].filter(Boolean).join(" · ");

    return {
      title,
      description,
      type: copy.searchTypeProject,
      path: `/projects/${item.id}`,
      text: [title, description, item.id].filter(Boolean).join(" "),
    };
  });

  const posts = blogList.map((post) => {
    const title = localizeField(post, "title", language);

    return {
      title,
      description: post.date,
      type: copy.searchTypeBlog,
      path: `/blog/${post.id}`,
      text: [title, post.date, post.id].filter(Boolean).join(" "),
    };
  });

  const certificateItems = certificates.map((certificate) => {
    const title = localizeField(certificate, "title", language);
    const issuer = localizeField(certificate, "issuer", language);
    const description = [issuer, localizeDateLabel(certificate.issued, language)].filter(Boolean).join(" · ");

    return {
      title,
      description,
      type: copy.searchTypeCertificate,
      path: `/certificates/${getCertificateSlug(certificate)}`,
      text: [title, issuer, certificate.issued, certificate.id].filter(Boolean).join(" "),
    };
  });

  const experience = experienceList.map((item) => {
    const title = localizeField(item, "title", language);
    const company = localizeField(item, "company", language);
    const date = localizeDateLabel(item.date, language);

    return {
      title,
      description: [company, date].filter(Boolean).join(" · "),
      type: copy.searchTypeExperience,
      path: "/experience",
      text: [title, company, date].filter(Boolean).join(" "),
    };
  });

  const studies = studyPath.map((item) => {
    const title = localizeField(item, "title", language);
    const school = localizeField(item, "school", language);

    return {
      title,
      description: [school, item.date].filter(Boolean).join(" · "),
      type: copy.searchTypeStudy,
      path: "/study",
      text: [title, school, item.date].filter(Boolean).join(" "),
    };
  });

  return [...pages, ...projects, ...posts, ...certificateItems, ...experience, ...studies].map((item) => ({
    ...item,
    searchText: normalize(`${item.title} ${item.description || ""} ${item.text || ""}`),
  }));
}

function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function getMatchScore(text, query) {
  if (text === query) {
    return 100;
  }

  if (text.startsWith(query)) {
    return 80;
  }

  if (text.includes(query)) {
    return 50;
  }

  const words = query.split(/\s+/).filter(Boolean);
  const matchedWords = words.filter((word) => text.includes(word)).length;

  return matchedWords === words.length ? 30 + matchedWords : 0;
}

function searchTriggerStyle(t) {
  return {
    alignItems: "center",
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: 8,
    color: t.dim,
    cursor: "pointer",
    display: "flex",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    gap: 10,
    justifyContent: "space-between",
    minHeight: 36,
    padding: "7px 9px 7px 11px",
    width: "100%",
  };
}

function kbdStyle(t) {
  return {
    background: t.surface2,
    border: `1px solid ${t.borderLight}`,
    borderRadius: 6,
    color: t.dim,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    lineHeight: 1,
    padding: "4px 6px",
  };
}

function closeButtonStyle(t) {
  return {
    background: "transparent",
    border: 0,
    color: t.dim,
    cursor: "pointer",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    padding: "0 16px",
  };
}

function resultButtonStyle(t) {
  return {
    background: "transparent",
    border: 0,
    borderBottom: `1px solid ${t.borderLight}`,
    color: "inherit",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    textAlign: "left",
    width: "100%",
  };
}

function resultMetaStyle(t) {
  return {
    color: t.dim,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    textTransform: "uppercase",
  };
}

function resultTitleStyle(t) {
  return {
    color: t.text,
    fontSize: 16,
  };
}

function resultDescriptionStyle(t) {
  return {
    color: t.muted,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  };
}
